import { BookingConfiguration } from '@components/shared/booking-configurator/booking-configurator.config.types'
import { ReservationDetails } from '@graphql/queries/reservationDetails'
import {
  ListingType,
  PublicListingFragment,
  ListingPriceFragment,
  PricingScheme,
} from '@libs/graphql/types'
import Analytics from '@services/Analytics'
import {
  GlobalListingType,
  UserAddGuestsDisplayed,
  PricingStructure,
  ListListingImpressionAISearchPriceType,
} from '@services/Analytics/tracking-plan/segment'
import { Localization } from '@services/localization/localization'
import { listingPriceUtils } from '@utils/listing-price'

export const trackUserAddGuestsDisplayed = ({
  listing,
  guestsDisplayedParams,
  configuration,
  reservation,
}: {
  listing: Pick<
    PublicListingFragment,
    | 'currentPoolPrice'
    | 'country_code'
    | 'default_instant_booking'
    | 'max_guests'
    | 'id'
    | 'instant_group_size'
    | 'discount_days'
  >
  guestsDisplayedParams: NonNullable<
    Pick<
      UserAddGuestsDisplayed,
      | 'checkoutId'
      | 'searchId'
      | 'enteredFromContactHostFlow'
      | 'enteredFromReservationEdit'
      | 'enteredFromPLP'
      | 'enteredFrom'
    >
  >
  configuration?: BookingConfiguration
  reservation?: Pick<
    ReservationDetails,
    'adult_guests' | 'children_guests' | 'infant_guests' | 'pet_guests'
  >
}) => {
  let reservationConfig = {}
  if (reservation) {
    reservationConfig = {
      defaultAdultNumber: reservation?.adult_guests ?? 1,
      defaultChildrenNumber: reservation?.children_guests ?? 0,
      infants: reservation?.infant_guests ?? 0,
      defaultPetNumber: reservation?.pet_guests ?? 0,
    }
  }

  const localizationService = Localization(listing?.country_code ?? undefined)
  const priceScheme = listing?.currentPoolPrice?.pricing_scheme
  const isDaily = priceScheme === 'DAILY' || priceScheme === 'DAILY_TIERED'

  const totalPriceDisplayed = listingPriceUtils.getDisplayPrice({
    currentPoolPrice: listing?.currentPoolPrice,
    date: configuration?.date,
    discountDays: listing?.discount_days,
    guestCount: Object.values(configuration?.guests ?? {}).reduce(
      (acc, curr) => acc + curr,
      0
    ),
  })

  const additionalGuestFeesDisplayedInfo = {
    numberOfGuestsExtraFeeAppliesPast:
      listing?.currentPoolPrice?.price_per_guest_threshold ?? 0,
    pricePerAdditionalGuest: listing?.currentPoolPrice?.price_per_guest ?? 0,
  }

  Analytics.trackEvent('UserAddGuestsDisplayed', {
    priceType: isDaily
      ? ListListingImpressionAISearchPriceType.Daily
      : ListListingImpressionAISearchPriceType.Hourly,
    currency: localizationService.getCurrency(),
    additionalGuestFeesDisplayedInfo,
    defaultAdultNumber: configuration?.guests?.adults ?? 1,
    defaultChildrenNumber: configuration?.guests?.children ?? 0,
    defaultInfantNumber: configuration?.guests?.infants ?? 0,
    defaultPetNumber: configuration?.guests?.pets ?? 0,
    listingHasInstantBooking: listing?.default_instant_booking ?? false,
    listingId: listing?.id ?? '',
    poolMaximumGuests: listing?.max_guests ?? 0,
    instantBookingGuestLimit: listing?.instant_group_size ?? 0,
    initialTotalPriceDisplayed: 0,
    isMultiDip: false,
    isInstantBookingEligible: listing?.default_instant_booking ?? false,
    price: totalPriceDisplayed,
    ...guestsDisplayedParams,
    ...reservationConfig,
  })
}

export const getGlobalListingType = (listingType?: ListingType | null) => {
  if (!listingType) return GlobalListingType.Other

  const values = Object.values(GlobalListingType)
  return values.find((type) => type === listingType) ?? GlobalListingType.Other
}

export const getPricingStructure = (
  currentPoolPrice?: Pick<ListingPriceFragment, 'pricing_scheme'> | null
) => {
  if (!currentPoolPrice) return PricingStructure.FlatRate

  const pricingScheme = currentPoolPrice.pricing_scheme

  const { [pricingScheme]: scheme }: Record<PricingScheme, PricingStructure> = {
    PER_GUEST: PricingStructure.FlatRate,
    TIERED: PricingStructure.TieredPricing,
    DAILY: PricingStructure.FullDaySingle,
    DAILY_TIERED: PricingStructure.FullDayTiered,
  }

  return scheme
}
